import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useId,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { Link } from 'react-router-dom';
import { createPortal } from 'react-dom';
import { Bookmark, Check } from 'lucide-react';
import { api } from '../api.js';
import { useAccount } from '../Account.js';

export type Collection = 'WANT_TO_TRY' | 'ALREADY_OWN';
export const labels: Record<Collection, string> = {
  WANT_TO_TRY: 'Want to try',
  ALREADY_OWN: 'Already own',
};
export type CollectionFragrance = {
  id: string;
  slug: string;
  name: string;
  concentration: string;
  isOriginal: boolean;
  brand: { name: string } | null;
  media: { id: string; alt: string } | null;
};
type SavedItem = {
  fragranceId: string;
  collection: Collection;
  createdAt: string;
  updatedAt: string;
  fragrance: CollectionFragrance | null;
};
/** One collection row: bottle thumbnail, brand, name and concentration, plus optional controls. */
export function CollectionRow({
  fragrance,
  children,
}: {
  fragrance: CollectionFragrance | null;
  children?: ReactNode;
}) {
  return (
    <li>
      <div className="saved-fragrance-photo">
        {fragrance?.media ? (
          <img
            src={'/media/' + encodeURIComponent(fragrance.media.id) + '?size=thumb'}
            alt={fragrance.media.alt}
            loading="lazy"
          />
        ) : (
          <Bookmark size={19} aria-hidden />
        )}
      </div>
      <div className="saved-fragrance-details">
        {fragrance ? (
          <>
            <p>
              {fragrance.brand?.name}
            </p>
            <h3>
              <Link to={'/fragrances/' + encodeURIComponent(fragrance.slug)}>{fragrance.name}</Link>
            </h3>
            <p>{fragrance.concentration}</p>
          </>
        ) : (
          <>
            <h3>Fragrance unavailable</h3>
            <p>
              This fragrance is no longer in the public catalogue. You can remove it from your
              collection.
            </p>
          </>
        )}
        {children}
      </div>
    </li>
  );
}
type SavedState = {
  items: SavedItem[];
  loading: boolean;
  error: string;
  pending: Set<string>;
  reload: () => Promise<void>;
  save: (id: string, collection: Collection | null) => Promise<void>;
};
const SavedContext = createContext<SavedState>({
  items: [],
  loading: false,
  error: '',
  pending: new Set(),
  reload: async () => {},
  save: async () => {},
});

export function SavedFragrancesProvider({ children }: { children: ReactNode }) {
  const { user } = useAccount();
  // A separate keyed store prevents one account's private state surviving an account switch.
  return (
    <SavedStore key={user?.id ?? 'guest'} signedIn={Boolean(user)}>
      {children}
    </SavedStore>
  );
}
function SavedStore({ signedIn, children }: { signedIn: boolean; children: ReactNode }) {
  const [items, setItems] = useState<SavedItem[]>([]),
    [loading, setLoading] = useState(signedIn),
    [error, setError] = useState('');
  const [pending, setPending] = useState(new Set<string>()),
    locks = useRef(new Set<string>()),
    version = useRef(0);
  const reload = useCallback(async () => {
    if (!signedIn) return;
    const current = ++version.current;
    setLoading(true);
    setError('');
    try {
      const result = await api<{ items: SavedItem[] }>('/api/v1/saved-fragrances');
      if (current === version.current) setItems(result.items);
    } catch (failure) {
      if (current === version.current) setError((failure as Error).message);
    } finally {
      if (current === version.current) setLoading(false);
    }
  }, [signedIn]);
  useEffect(() => {
    void reload();
    return () => {
      version.current++;
    };
  }, [reload]);
  async function save(id: string, collection: Collection | null) {
    if (locks.current.has(id)) return;
    locks.current.add(id);
    setPending(new Set(locks.current));
    try {
      const session = await api<{ csrfToken: string }>('/api/v1/account/session');
      await api('/api/v1/saved-fragrances/' + encodeURIComponent(id), {
        method: collection ? 'PUT' : 'DELETE',
        headers: { 'Content-Type': 'application/json', 'X-CSRF-Token': session.csrfToken },
        body: JSON.stringify(collection ? { collection } : {}),
      });
      await reload();
    } finally {
      locks.current.delete(id);
      setPending(new Set(locks.current));
    }
  }
  return (
    <SavedContext.Provider value={{ items, loading, error, pending, reload, save }}>
      {children}
    </SavedContext.Provider>
  );
}

export function SaveFragrance({
  fragrance,
  unavailable = false,
  compact = false,
}: {
  fragrance: { id: string; name: string };
  unavailable?: boolean;
  compact?: boolean;
}) {
  const { user } = useAccount(),
    state = useContext(SavedContext),
    id = useId();
  const [open, setOpen] = useState(false),
    [error, setError] = useState(''),
    [message, setMessage] = useState('');
  const saved = state.items.find((item) => item.fragranceId === fragrance.id),
    busy = state.pending.has(fragrance.id);
  const trigger = useRef<HTMLButtonElement>(null),
    panel = useRef<HTMLDivElement>(null);
  const [position, setPosition] = useState({ top: 0, left: 0 });
  useEffect(() => {
    if (!open) return;
    const update = () => {
      const rect = trigger.current?.getBoundingClientRect();
      if (rect)
        setPosition({
          top: Math.max(
            12,
            Math.min(
              rect.bottom + 8,
              window.innerHeight - (panel.current?.offsetHeight ?? 240) - 12,
            ),
          ),
          left: Math.max(12, Math.min(rect.right - 300, window.innerWidth - 312)),
        });
    };
    const outside = (event: PointerEvent) => {
      if (
        !panel.current?.contains(event.target as Node) &&
        !trigger.current?.contains(event.target as Node)
      )
        setOpen(false);
    };
    const escape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setOpen(false);
        trigger.current?.focus();
      }
    };
    update();
    panel.current?.querySelector<HTMLElement>('button,a')?.focus();
    window.addEventListener('resize', update);
    window.addEventListener('scroll', update, true);
    document.addEventListener('pointerdown', outside);
    document.addEventListener('keydown', escape);
    return () => {
      window.removeEventListener('resize', update);
      window.removeEventListener('scroll', update, true);
      document.removeEventListener('pointerdown', outside);
      document.removeEventListener('keydown', escape);
    };
  }, [open]);
  async function choose(collection: Collection | null) {
    setError('');
    setMessage('');
    try {
      await state.save(fragrance.id, collection);
      setMessage(
        collection ? 'Saved to ' + labels[collection] + '.' : 'Removed from your collections.',
      );
      setOpen(false);
      trigger.current?.focus();
    } catch (failure) {
      setError((failure as Error).message);
    }
  }
  return (
    <div className={'fragrance-save' + (compact ? ' compact' : '')}>
      <button
        ref={trigger}
        type="button"
        className={'fragrance-save-toggle' + (saved ? ' is-saved' : '')}
        title={(saved ? labels[saved.collection] + ': ' : 'Save ') + fragrance.name}
        aria-expanded={open}
        aria-controls={id}
        disabled={busy}
        onClick={() => {
          setOpen(!open);
          setMessage('');
        }}
        aria-label={(saved ? labels[saved.collection] + ': ' : 'Save ') + fragrance.name}
      >
        <Bookmark size={14} fill={saved ? 'currentColor' : 'none'} aria-hidden />
        {!compact && <span>{busy ? 'Saving…' : saved ? labels[saved.collection] : 'Save'}</span>}
      </button>
      {open &&
        createPortal(
          <div
            ref={panel}
            id={id}
            className="fragrance-save-options"
            style={{ position: 'fixed', ...position }}
            role="region"
            aria-label={'Save ' + fragrance.name}
          >
            <div className="save-options-heading">
              <strong>{fragrance.name}</strong>
              <button
                type="button"
                onClick={() => {
                  setOpen(false);
                  trigger.current?.focus();
                }}
              >
                Close
              </button>
            </div>
            {!user ? (
              <p>
                <Link to="/sign-in">Sign in</Link> or <Link to="/register">create an account</Link>{' '}
                to save fragrances to your collections.
              </p>
            ) : state.loading ? (
              <p role="status">Loading your collections…</p>
            ) : state.error ? (
              <p role="alert">
                {state.error}{' '}
                <button type="button" onClick={() => void state.reload()}>
                  Try again
                </button>
              </p>
            ) : (
              <>
                <p>Save to your collection</p>
                <div className="fragrance-save-choices">
                  {!unavailable &&
                    (Object.keys(labels) as Collection[]).map((collection) => (
                      <button
                        type="button"
                        key={collection}
                        disabled={busy}
                        aria-pressed={saved?.collection === collection}
                        onClick={() => void choose(collection)}
                      >
                        {labels[collection]}
                      </button>
                    ))}
                  {saved && (
                    <button type="button" disabled={busy} onClick={() => void choose(null)}>
                      Remove
                    </button>
                  )}
                </div>
              </>
            )}
            {error && <p role="alert">{error}</p>}
          </div>,
          document.body,
        )}
      {message && (
        <p className={compact ? 'sr-only' : 'fragrance-save-message'} role="status">
          {message}
        </p>
      )}
    </div>
  );
}

export function MySavedFragrances() {
  const { items, loading, error, reload } = useContext(SavedContext);
  const { user } = useAccount();
  const [collection, setCollection] = useState<Collection>('WANT_TO_TRY');
  const visible = items.filter((item) => item.collection === collection);
  return (
    <section className="saved-collections" aria-labelledby="saved-heading">
      <h2 id="saved-heading">Your fragrance collections</h2>
      <p>
        Keep a wishlist of fragrances to try and a collection of the ones you own.{' '}
        {user?.isPublic
          ? 'Both lists appear on your public profile.'
          : 'Your profile is private, so only you can see them.'}
      </p>
      <div className="collection-switch" role="group" aria-label="Choose a collection">
        {(Object.keys(labels) as Collection[]).map((value) => (
          <button
            type="button"
            key={value}
            aria-pressed={collection === value}
            onClick={() => setCollection(value)}
          >
            {labels[value]} <span>{items.filter((item) => item.collection === value).length}</span>
          </button>
        ))}
      </div>
      {loading && <p role="status">Loading your collections…</p>}
      {error && (
        <p role="alert">
          {error}{' '}
          <button type="button" onClick={() => void reload()}>
            Try again
          </button>
        </p>
      )}
      {!loading &&
        !error &&
        (visible.length ? (
          <ul className="saved-fragrance-list">
            {visible.map((item) => (
              <CollectionRow key={item.fragranceId} fragrance={item.fragrance}>
                <SaveFragrance
                  fragrance={{
                    id: item.fragranceId,
                    name: item.fragrance?.name ?? 'unavailable fragrance',
                  }}
                  unavailable={!item.fragrance}
                />
              </CollectionRow>
            ))}
          </ul>
        ) : (
          <div className="collection-empty">
            <h3>
              {collection === 'WANT_TO_TRY'
                ? 'What would you like to try next?'
                : 'Build your fragrance shelf'}
            </h3>
            <p>
              {collection === 'WANT_TO_TRY'
                ? 'Save a fragrance to “Want to try” as you explore.'
                : 'Save fragrances to “Already own”, or move them here from your wishlist.'}
            </p>
            <Link className="button secondary" to="/">
              Explore fragrances
            </Link>
          </div>
        ))}
    </section>
  );
}
